import { Helmet } from "react-helmet-async";

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface ProductSchema {
  name: string;
  description: string;
  price: string;
  currency?: string;
  orderLink?: string;
  ratingValue?: string;
  reviewCount?: string;
}

interface SchemaMarkupProps {
  type: "home" | "product" | "faq" | "contact" | "about" | "page";
  breadcrumbs?: BreadcrumbItem[];
  faqs?: FAQItem[];
  products?: ProductSchema[];
  pageName?: string;
  pageDescription?: string;
}

const siteUrl = "https://webhostbuybd.com";

const buildOrganizationSchema = () => ({
  "@type": "Organization",
  "@id": `${siteUrl}/#organization`,
  name: "Web Host Buy BD",
  url: siteUrl,
  logo: {
    "@type": "ImageObject",
    "@id": `${siteUrl}/#logo`,
    url: `${siteUrl}/logo.png`,
    contentUrl: `${siteUrl}/logo.png`,
    width: 512,
    height: 512,
    caption: "Web Host Buy BD",
  },
  image: { "@id": `${siteUrl}/#logo` },
  description:
    "Bangladesh's leading BDIX web hosting provider offering shared hosting, VPS, dedicated servers, and domain registration with 99.99% uptime.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "10 Nur Vobon (2nd Floor), Nogor Vobon Road",
    addressLocality: "Manikganj",
    postalCode: "1800",
    addressCountry: "BD",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+880-9649-660000",
      email: "hello@webhostbuybd.com",
      contactType: "customer service",
      availableLanguage: ["English", "Bengali"],
      areaServed: "BD",
    },
    {
      "@type": "ContactPoint",
      telephone: "+880-9649-660000",
      contactType: "sales",
      availableLanguage: ["English", "Bengali"],
      areaServed: "BD",
    },
  ],
  sameAs: [
    "https://www.facebook.com/WebHostBuyBD",
    "https://www.linkedin.com/company/WebHostBuyBD",
    "https://x.com/WebHostBuyBD",
    "https://www.youtube.com/@WebHostBuyBD",
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    bestRating: "5",
    worstRating: "1",
    ratingCount: "487",
    reviewCount: "312",
  },
});

const buildWebSiteSchema = () => ({
  "@type": "WebSite",
  "@id": `${siteUrl}/#website`,
  url: siteUrl,
  name: "Web Host Buy BD",
  description: "Best BDIX Web Hosting Provider in Bangladesh",
  publisher: { "@id": `${siteUrl}/#organization` },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${siteUrl}/domain?search={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
  inLanguage: "en-BD",
});

const buildLocalBusinessSchema = () => ({
  "@type": "LocalBusiness",
  "@id": `${siteUrl}/#localbusiness`,
  name: "Web Host Buy BD",
  image: `${siteUrl}/logo.png`,
  url: siteUrl,
  telephone: "+880-9649-660000",
  email: "hello@webhostbuybd.com",
  address: {
    "@type": "PostalAddress",
    streetAddress: "10 Nur Vobon (2nd Floor), Nogor Vobon Road",
    addressLocality: "Manikganj",
    postalCode: "1800",
    addressCountry: "BD",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 23.8617,
    longitude: 90.0047,
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday"],
    opens: "10:00",
    closes: "18:00",
  },
  priceRange: "৳৳",
  sameAs: [
    "https://www.facebook.com/WebHostBuyBD",
    "https://www.linkedin.com/company/WebHostBuyBD",
    "https://x.com/WebHostBuyBD",
    "https://www.youtube.com/@WebHostBuyBD",
  ],
});

const buildReviewsSchema = () => [
  {
    "@type": "Review",
    author: { "@type": "Person", name: "Rahim Ahmed" },
    datePublished: "2024-06-15",
    reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
    reviewBody:
      "Web Host Buy BD has been instrumental in our online success. The BDIX connectivity is blazing fast and their support team is incredibly responsive.",
  },
  {
    "@type": "Review",
    author: { "@type": "Person", name: "Fatima Khan" },
    datePublished: "2024-05-20",
    reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
    reviewBody:
      "Switched to Web Host Buy BD 2 years ago and never looked back. Our e-commerce site loads instantly and we've had zero downtime.",
  },
  {
    "@type": "Review",
    author: { "@type": "Person", name: "Kamal Hossain" },
    datePublished: "2024-04-10",
    reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
    reviewBody:
      "Best hosting provider in Bangladesh! The NVMe SSDs make a huge difference. My clients' websites are faster than ever.",
  },
  {
    "@type": "Review",
    author: { "@type": "Person", name: "Nasreen Begum" },
    datePublished: "2024-03-05",
    reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
    reviewBody:
      "Affordable prices, excellent features, and top-notch support. Highly recommend for anyone looking for quality hosting in Bangladesh.",
  },
];

const buildItemListSchema = () => ({
  "@type": "ItemList",
  "@id": `${siteUrl}/#servicelist`,
  name: "Web Host Buy BD Hosting Services",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "BDIX Web Hosting",
      url: `${siteUrl}/bdix-web-hosting`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "BDIX Turbo Hosting",
      url: `${siteUrl}/bdix-turbo-hosting`,
    },
    {
      "@type": "ListItem",
      position: 3,
      name: "BDIX Unmanaged VPS",
      url: `${siteUrl}/bdix-vps`,
    },
    {
      "@type": "ListItem",
      position: 4,
      name: "BDIX Managed VPS",
      url: `${siteUrl}/bdix-managed-vps`,
    },
    {
      "@type": "ListItem",
      position: 5,
      name: "BDIX Dedicated Server",
      url: `${siteUrl}/bdix-dedicated-server`,
    },
    {
      "@type": "ListItem",
      position: 6,
      name: "BDIX Reseller Hosting",
      url: `${siteUrl}/bdix-reseller-hosting`,
    },
    {
      "@type": "ListItem",
      position: 7,
      name: "Domain Registration",
      url: `${siteUrl}/domain`,
    },
  ],
});

const buildMerchantReturnPolicy = () => ({
  "@type": "MerchantReturnPolicy",
  "@id": `${siteUrl}/#returnpolicy`,
  applicableCountry: "BD",
  returnPolicyCategory:
    "https://schema.org/MerchantReturnFiniteReturnWindow",
  merchantReturnDays: 7,
  returnMethod: "https://schema.org/ReturnByMail",
  returnFees: "https://schema.org/FreeReturn",
});

const buildShippingDetails = () => ({
  "@type": "OfferShippingDetails",
  "@id": `${siteUrl}/#shipping`,
  shippingRate: {
    "@type": "MonetaryAmount",
    value: "0",
    currency: "BDT",
  },
  shippingDestination: {
    "@type": "DefinedRegion",
    addressCountry: "BD",
  },
  deliveryTime: {
    "@type": "ShippingDeliveryTime",
    handlingTime: {
      "@type": "QuantitativeValue",
      minValue: 0,
      maxValue: 0,
      unitCode: "DAY",
    },
    transitTime: {
      "@type": "QuantitativeValue",
      minValue: 0,
      maxValue: 0,
      unitCode: "DAY",
    },
  },
});

const buildProductSchemas = (products: ProductSchema[], pageUrl: string) =>
  products.map((product, index) => {
    const cleanPrice = product.price.replace(/[^0-9.]/g, "");
    const productId = `${siteUrl}${pageUrl}#product-${index}`;
    const offerId = `${siteUrl}${pageUrl}#offer-${index}`;

    return {
      "@type": "Product",
      "@id": productId,
      name: product.name,
      description: product.description,
      image: `${siteUrl}/logo.png`,
      brand: {
        "@type": "Brand",
        name: "Web Host Buy BD",
      },
      category: "Web Hosting Services",
      offers: {
        "@type": "Offer",
        "@id": offerId,
        price: cleanPrice,
        priceCurrency: product.currency || "BDT",
        availability: "https://schema.org/InStock",
        priceValidUntil: "2099-12-31",
        url:
          product.orderLink || `${siteUrl}${pageUrl}`,
        seller: { "@id": `${siteUrl}/#organization` },
        shippingDetails: { "@id": `${siteUrl}/#shipping` },
        hasMerchantReturnPolicy: { "@id": `${siteUrl}/#returnpolicy` },
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: product.ratingValue || "4.8",
        bestRating: "5",
        worstRating: "1",
        ratingCount: product.reviewCount || "150",
        reviewCount: product.reviewCount || "142",
      },
    };
  });

const buildFAQSchema = (faqs: FAQItem[]) => ({
  "@type": "FAQPage",
  "@id": `${siteUrl}/#faq`,
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

const buildBreadcrumbSchema = (breadcrumbs: BreadcrumbItem[]) => ({
  "@type": "BreadcrumbList",
  "@id": `${siteUrl}${breadcrumbs[breadcrumbs.length - 1]?.url || "/"}#breadcrumb`,
  itemListElement: breadcrumbs.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: `${siteUrl}${item.url}`,
  })),
});

const buildWebPageSchema = (
  pageName: string,
  pageDescription: string | undefined,
  breadcrumbs?: BreadcrumbItem[]
) => {
  const pageUrl = breadcrumbs?.[breadcrumbs.length - 1]?.url || "/";
  return {
    "@type": "WebPage",
    "@id": `${siteUrl}${pageUrl}#webpage`,
    url: `${siteUrl}${pageUrl}`,
    name: pageName,
    description: pageDescription,
    isPartOf: { "@id": `${siteUrl}/#website` },
    about: { "@id": `${siteUrl}/#organization` },
    breadcrumb: breadcrumbs
      ? { "@id": `${siteUrl}${pageUrl}#breadcrumb` }
      : undefined,
    inLanguage: "en-BD",
    datePublished: "2024-01-01",
    dateModified: new Date().toISOString().split("T")[0],
  };
};

const SchemaMarkup = ({
  type,
  breadcrumbs,
  faqs,
  products,
  pageName,
  pageDescription,
}: SchemaMarkupProps) => {
  const pageUrl = breadcrumbs?.[breadcrumbs.length - 1]?.url || "/";

  // Build @graph array
  const graph: Record<string, unknown>[] = [
    buildOrganizationSchema(),
    buildWebSiteSchema(),
  ];

  // Home page extras
  if (type === "home") {
    graph.push(buildLocalBusinessSchema());
    graph.push(buildItemListSchema());
    graph.push(buildMerchantReturnPolicy());
    graph.push(buildShippingDetails());

    // Attach reviews to org
    const orgIndex = 0;
    (graph[orgIndex] as Record<string, unknown>).review = buildReviewsSchema();
  }

  // Breadcrumbs
  if (breadcrumbs && breadcrumbs.length > 0) {
    graph.push(buildBreadcrumbSchema(breadcrumbs));
  }

  // WebPage
  if (pageName) {
    graph.push(buildWebPageSchema(pageName, pageDescription, breadcrumbs));
  }

  // FAQ
  if (faqs && faqs.length > 0) {
    graph.push(buildFAQSchema(faqs));
  }

  // Products
  if (products && products.length > 0) {
    if (type !== "home") {
      graph.push(buildMerchantReturnPolicy());
      graph.push(buildShippingDetails());
    }
    graph.push(...buildProductSchemas(products, pageUrl));
  }

  const graphSchema = {
    "@context": "https://schema.org",
    "@graph": graph,
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(graphSchema)}
      </script>
    </Helmet>
  );
};

export default SchemaMarkup;
